<template>
  <Spinner v-if="loading" />
  <main class="home" v-if="!loading">
    <HeaderM3
      :logoLinkRoute="M3_Welcome"
      :showSuspiciousActivityButton="hasSuspiciousActivityMerchantFlag"
    />
    <div class="main-body d-flex flex-row">
      <div class="w-100">
        <NavigationBar v-if="!isD2C && !isEcommerceFlow" :current-step="currentStep" />
        <SaleTimeOut />
        <div class="container p-0 container">
          <div
            class="row h-100 d-flex flex-row align-items-center justify-content-between"
          >
            <div
              class="col-md-8 offset-md-2 col-lg-6 offset-lg-3 col-xl-4 offset-xl-4"
            >
              <div>
                <div>
                  <h2 class="text-center fs-5 mt-5">
                    {{ dobGreeting }}
                  </h2>
                  <p class="text-center text-muted mt-2">
                    {{ t("KYC_REUSE_DOB_SUBTITLE") }}
                  </p>
                  <form class="mt-4" @submit.prevent>
                    <div class="mb-3">
                      <SimplifiedDateInput
                        ref="dobComponent"
                        :label-name="t('DATE_OF_BIRTH')"
                        v-model="birthdayFieldString"
                        :required="true"
                        :max-date="eighteenYearsAgo"
                        :external-error="kycReuseDobError"
                        @valid="handleDateValidation"
                      />
                    </div>
                  </form>
                </div>
                <div class="fixed-bottom-section">
                  <div
                    class="alert alert-info d-flex align-items-center"
                    role="alert"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="d-none">
                      <symbol id="info-circle-fill" viewBox="0 0 16 16">
                        <path
                          d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"
                        />
                      </symbol>
                    </svg>
                    <svg
                      class="bi flex-shrink-0 me-2"
                      role="img"
                      aria-label="Info:"
                    >
                      <use xlink:href="#info-circle-fill" />
                    </svg>
                    <div v-if="isIdFlow">
                      {{ t("KYC_REUSE_MATCHED_ID_NUMBER_WARNING") }}
                    </div>
                    <div v-else>
                      {{ t("KYC_REUSE_MATCHED_PHONE_NUMBER_WARNING") }}
                    </div>
                  </div>
                  <ButtonContinue
                    class="btn-next w-100"
                    :isEnabled="isContinueEnabled"
                    :isLoading="loading"
                    @callback="onContinue"
                  >
                    <span v-if="!loading">{{ t("CONTINUE") }}</span>
                    <span v-else>
                      <i class="fa fa-spinner fa-spin"></i> {{ t("LOADING") }}
                    </span>
                  </ButtonContinue>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <InConsoleSidebar />
    </div>
  </main>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { datadogRum } from "@datadog/browser-rum";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import {
  productVersionRecordingAppsMapper,
  recorder,
} from "@/apps/merchant3/shared-services/recording.service";
import useCartPersistState from "@/apps/merchant3/hooks/useCartPersistState";
import { RoutesPageSteps } from "@/apps/merchant3/router/RoutesPageSteps";
import MerchantFlags from "@/apps/merchant3/types/MerchantFlags";
import {
  isEcommerceFlow,
  isEsignV2Country,
} from "@/apps/merchant3/helpers/checkoutFlows";
import {
  attemptKyc,
  attemptKycIdNumber,
} from "@/apps/merchant3/services/lookup-persons.service";
import { useTermsAndPrivacyAcceptance } from "@/apps/merchant3/hooks/useTermsAndPrivacyAcceptance";
import { useConfigurableNavigation } from "@/apps/merchant3/hooks/useConfigurableNavigation";
import { getCountryCode } from "@/apps/merchant3/helpers/flowType";
import ButtonContinue from "@/apps/merchant3/shared-components/ui/ButtonContinue.vue";
import HeaderM3 from "@/apps/merchant3/components/HeaderM3.vue";
import InConsoleSidebar from "@/apps/merchant3/components/in-console/ui/InConsoleSidebar.vue";
import NavigationBar from "@/apps/merchant3/shared-components/common/NavigationBar.vue";
import SimplifiedDateInput from "@/apps/merchant3/components/ui/input-fields/SimplifiedDateInput.vue";
import SaleTimeOut from "@/apps/merchant3/shared-components/common/SaleTimeout.vue";
import Spinner from "@/apps/merchant3/shared-components/ui/Spinner.vue";
import { useCheckoutNavigation } from "@/apps/merchant3/hooks/useCheckoutNavigation";
import { isDirectToCustomerSessionSet } from "@/apps/merchant3/helpers/navigationGuards";

const { t } = useI18n();
const M3_Welcome = ref(RouteName.M3Welcome);
document.title = "PayJoy - " + t("PAGE_TITLE_PRIVACY_POLICY");
const store = useStore();
const currentStep = ref(1);
const app = getCurrentInstance()?.appContext.app;
const loading = ref(true);
const isCreditCheckRequired = computed(() => isEsignV2Country());
const router = useRouter();
const retryLimit = 2;

const isContinueEnabled = ref(false);
const retryCount = ref(0);
const kycReuseDobError = ref("");

const birthdayFieldString = ref("");
const dobComponent = ref();

const eighteenYearsAgo = new Date();
eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);

const hasSuspiciousActivityMerchantFlag = computed(() =>
  store.state.merchant?.merchantFlags?.includes(
    MerchantFlags.SHOW_SUSPICIOUS_SALE_FLAG
  )
);

const isD2C = computed(() => isDirectToCustomerSessionSet());
const { handleContinueToNextPage } = useTermsAndPrivacyAcceptance();
const { isConfigurableFlow, navigateToNextStepWithRefresh } =
  useConfigurableNavigation();

const updateRecorder = () => {
  recorder(app)?.updateRecordingInfo(store.state.carts.userId, {
    cartTag: store.state.carts.cartTag,
    merchantId: store.state.carts.merchantId,
    clerkId: store.state.carts.salesClerkId,
    productVersion: store.state.carts.productVersion,
    country: store.state.merchant?.merchant?.country,
  });
};

const handleDateValidation = (isValid: boolean) => {
  isContinueEnabled.value = isValid;
};

const isIdFlow = computed(() => {
  return sessionStorage.getItem("kycReuseOrigin") === "idNumber";
});

const dobGreeting = computed(() => {
  const firstName = store.state.customer?.firstName || "";
  if (firstName) {
    return t("KYC_REUSE_DOB_GREETING_WITH_NAME", { name: firstName });
  }
  return t("KYC_REUSE_DOB_GREETING");
});

const { navigateToNextSteps } = useCheckoutNavigation();

const onContinue = async () => {
  if (loading.value) return;

  const cartTag = store.state.carts.cartTag;
  const origin = sessionStorage.getItem("kycReuseOrigin");
  const kycReusePhoneNumber = sessionStorage.getItem("kycReusePhoneNumber");
  const kycReuseIdNumber = sessionStorage.getItem("kycReuseIdNumber");
  const dob = birthdayFieldString.value; // Already in YYYY-MM-DD format
  kycReuseDobError.value = "";
  loading.value = true;
  const dateOfBirthLookupStart = performance.now();
  let isMatchSuccess = false;

  try {
    let result;

    if (origin === "idNumber" && kycReuseIdNumber) {
      result = await attemptKycIdNumber(cartTag, kycReuseIdNumber, dob);
    } else if (origin === "phoneNumber" && kycReusePhoneNumber) {
      result = await attemptKyc(cartTag, kycReusePhoneNumber, dob);
    }

    if (result.match) {
      isMatchSuccess = true;
      retryCount.value = 0;
      sessionStorage.removeItem("kycReuseIdNumber");
      sessionStorage.removeItem("kycReusePhoneNumber");

      // For configurable flow, use backend-driven navigation with config refresh
      // The refresh will get updated nextStep which accounts for SKIPPED_KYC
      if (isConfigurableFlow.value) {
        await navigateToNextStepWithRefresh("POLICY");
        return;
      }

      // Non-configurable flow: use existing frontend-driven navigation
      if (isCreditCheckRequired.value) {
        router.push({ name: RouteName.M3CreditCheckPermissionView, params: { countryCode: getCountryCode() } });
      } else {
        navigateToNextSteps();
      }
      return;
    } else {
      loading.value = false;
      retryCount.value += 1;
      kycReuseDobError.value = t("ERROR_DATE_OF_BIRTH_INVALID_TRY_AGAIN");
      if (retryCount.value >= retryLimit) {
        retryCount.value = 0;
        birthdayFieldString.value = "";
        if (isConfigurableFlow.value) {
          await navigateToNextStepWithRefresh("POLICY");
        } else {
          handleContinueToNextPage();
        }
      }

      return;
    }
  } catch (error) {
    retryCount.value += 1;
    if (retryCount.value >= retryLimit) {
      retryCount.value = 0;
      if (isConfigurableFlow.value) {
        await navigateToNextStepWithRefresh("POLICY");
      } else {
        handleContinueToNextPage();
      }
      return;
    }
  } finally {
    // Keep track of how long it took to lookup person by date of birth in RUM.
    const dateOfBirthLookupDuration =
      performance.now() - dateOfBirthLookupStart;
    datadogRum.addAction("m3-api-dob-lookup-finished", {
      loadTime: dateOfBirthLookupDuration,
    });

    if (!isMatchSuccess) {
      loading.value = false;
    }
  }
};

onMounted(() => {
  useCartPersistState(() => {
    store.dispatch("carts/setStep", RoutesPageSteps.DateOfBirthVerification);
    const recordingApp = productVersionRecordingAppsMapper(
      store.state.carts.productVersion
    );
    recorder(app)?.startRecording(recordingApp);
    updateRecorder();
    loading.value = false;
    return true;
  });
  loading.value = false;
});
</script>

<style scoped lang="scss">
.main-body {
  background-color: white;
}
.bi {
  width: 1em;
  height: 1em;
  fill: currentcolor;
}
.dob-content {
  padding-bottom: 220px;
}
.fixed-bottom-section {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  padding: 16px 24px 32px;
  z-index: 10;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}
.fixed-bottom-section :deep(.btn-next) {
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  display: block;
}
</style>
